import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _Array$isArray from '@babel/runtime-corejs3/core-js-stable/array/is-array';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { ArrowDownUpIcon, ArrowUpIcon, ArrowDownIcon, iconClassName } from '@thisisbud/bui-react-icons';
import { h1ClassName, h3ClassName, h2ClassName, paragraphClassName, h3Css } from '@thisisbud/bui-react-typography';
import { jsx, jsxs } from 'react/jsx-runtime';

const tableClassName = 'bui-table';
const tableCaptionClassName = `${tableClassName}__caption`;
const tableRowClassName = `${tableClassName}__row`;
const tableCellClassName = `${tableClassName}__cell`;
const tableCellLeftAlignClassName = `${tableCellClassName}--left`;
const tableCellCenterAlignClassName = `${tableCellClassName}--center`;
const tableCellRightAlignClassName = `${tableCellClassName}--right`;
const tableCellP1ClassName = `${tableCellClassName}--p1`;
const tableCellP2ClassName = `${tableCellClassName}--p2`;
const tableCellP3ClassName = `${tableCellClassName}--p3`;
const tableCellP4ClassName = `${tableCellClassName}--p4`;
const tableCellP5ClassName = `${tableCellClassName}--p5`;
const tableHeadCellClassName = `${tableClassName}__head-cell`;
const tableHeadCellWrapperClassName = `${tableHeadCellClassName}__wrapper`;
const tableHeadCellTitleClassName = `${tableHeadCellClassName}__title`;
const tableHeadCellActionClassName = `${tableHeadCellClassName}__action`;
const tableSortButtonClassName = `${tableClassName}__sort-button`;
const tableSortButtonActiveClassName = `${tableSortButtonClassName}--active`;
const tableBodyCellClassName = `${tableClassName}__body-cell`;
const tableFooterCellClassName = `${tableClassName}__footer-cell`;

/**
 * Render a caption or title for a table.
 *
 * @param props - The component props
 */
function TableCaption(props) {
  const {
    children
  } = props;
  return /*#__PURE__*/jsx("caption", {
    className: tableCaptionClassName,
    children: children
  });
}
TableCaption.displayName = 'TableCaption';

/**
 * Renders a table row.
 *
 * @param props - The component props
 */
function TableRow(props) {
  const {
    children,
    level
  } = props;
  return /*#__PURE__*/jsx("tr", {
    "aria-level": level,
    className: tableRowClassName,
    children: children
  });
}
TableRow.displayName = 'TableRow';

/**
 * Get the sort direction to be used the next time the sort button is clicked.
 *
 * @param props - The component props
 * @returns A sort direction value
 */
function getNextSortDirection(props) {
  const {
    defaultSortDirection,
    sortDirection
  } = props;
  switch (sortDirection) {
    case 'ascending':
      return 'descending';
    case 'descending':
      return 'ascending';
    default:
      return defaultSortDirection !== null && defaultSortDirection !== void 0 ? defaultSortDirection : 'ascending';
  }
}

/**
 * Get an icon component that represents the current sort direction.
 *
 * @param props - The component props
 * @returns An icon component
 */
function getSortIcon(props) {
  const {
    sortDirection
  } = props;
  switch (sortDirection) {
    case 'ascending':
      return ArrowDownIcon;
    case 'descending':
      return ArrowUpIcon;
    default:
      return ArrowDownUpIcon;
  }
}

/**
 * Renders a button that can be used to toggle the sort direction of a table column.
 *
 * @param props - The component props
 */
function TableSortButton(props) {
  const {
    columnKey,
    getLabel,
    id,
    onClick,
    sortDirection
  } = props;
  const Icon = getSortIcon(props); // eslint-disable-line @typescript-eslint/naming-convention
  const nextSortDirection = getNextSortDirection(props);
  const label = getLabel(columnKey, nextSortDirection);
  return /*#__PURE__*/jsx("button", {
    className: classnames(tableSortButtonClassName, typeof sortDirection === 'string' ? tableSortButtonActiveClassName : undefined),
    id: id,
    name: columnKey,
    type: "button",
    value: nextSortDirection,
    onClick: onClick,
    children: /*#__PURE__*/jsx(Icon, {
      label: label
    })
  });
}
TableSortButton.displayName = 'TableSortButton';

/**
 * Get a fully formed class name for a table cell to be used in the header, body or footer of the table.
 *
 * @param className - The class name of the the table cell
 * @param textAlignment - The text alignment of the contents of the table cell
 * @param priority - The priority level of the table cell
 * @returns A fully formed class name for a table cell
 */
function getCellClassName(className, textAlignment = 'left', priority = 1) {
  return classnames(className, tableCellClassName, `${tableCellClassName}--${textAlignment}`, `${tableCellClassName}--p${priority}`);
}

/**
 * Renders a table header cell.
 *
 * @param props - The component props
 */
function TableHeadCell(props) {
  const {
    children,
    columnKey,
    defaultSortDirection,
    getSortButtonLabel,
    id,
    onSortColumnClick,
    priority,
    sortable,
    sortDirection,
    textAlign
  } = props;
  return /*#__PURE__*/jsx("th", {
    "aria-sort": sortDirection,
    className: getCellClassName(tableHeadCellClassName, textAlign, priority),
    id: id,
    scope: "col",
    children: /*#__PURE__*/jsxs("span", {
      className: tableHeadCellWrapperClassName,
      children: [/*#__PURE__*/jsx("span", {
        className: tableHeadCellTitleClassName,
        children: children
      }), sortable ? /*#__PURE__*/jsx("span", {
        className: tableHeadCellActionClassName,
        children: /*#__PURE__*/jsx(TableSortButton, {
          columnKey: columnKey,
          defaultSortDirection: defaultSortDirection,
          getLabel: getSortButtonLabel,
          id: typeof id === 'string' ? `${id}-sort-btn` : undefined,
          sortDirection: sortDirection,
          onClick: onSortColumnClick
        })
      }) : null]
    })
  });
}
TableHeadCell.defaultProps = {
  textAlign: 'left'
};
TableHeadCell.displayName = 'TableHeadCell';

/**
 * Renders a table body cell.
 *
 * @param props - The component props
 */
function TableBodyCell(props) {
  const {
    children,
    priority,
    span,
    textAlign,
    rowHeader
  } = props;
  const Component = rowHeader === true ? 'th' : 'td'; // eslint-disable-line @typescript-eslint/naming-convention

  return /*#__PURE__*/jsx(Component, {
    className: getCellClassName(tableBodyCellClassName, textAlign, priority),
    colSpan: span,
    scope: rowHeader === true ? 'row' : undefined,
    children: children
  });
}
TableBodyCell.defaultProps = {
  textAlign: 'left'
};
TableBodyCell.displayName = 'TableBodyCell';

/**
 * Renders a table footer cell.
 *
 * @param props - The component props
 */
function TableFooterCell(props) {
  const {
    children,
    priority,
    span,
    textAlign
  } = props;
  return /*#__PURE__*/jsx("td", {
    className: getCellClassName(tableFooterCellClassName, textAlign, priority),
    colSpan: span,
    children: children
  });
}
TableFooterCell.defaultProps = {
  textAlign: 'left'
};
TableFooterCell.displayName = 'TableFooterCell';

let _ = t => t,
  _t;
// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledTableWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledTableWrapper",
  componentId: "sc-vb48io-0"
})(_t || (_t = _`
  position: relative;
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;

  & + &,
  .bui-notification + & {
    margin-top: ${0};
  }

  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }

  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    caption-side: top;
    empty-cells: show;
    min-width: 100%;
  }

  .${0} {
    ${0}
    margin-bottom: ${0};
    text-align: left;
  }

  .${0} {
    position: relative;
    border-style: solid;
    border-width: 0;
    vertical-align: middle;
    background-color: ${0};
    border-bottom-width: ${0};
    border-color: ${0};
    color: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    line-height: ${0};
    padding: ${0};
  }

  .${0} {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  .${0} {
    flex-basis: 100%;
    flex-shrink: 1;
    z-index: 2;
    pointer-events: none;
  }

  .${0} {
    margin-left: ${0};
  }

  .${0} {
    border-style: solid;
    border-width: 0;
    vertical-align: middle;
    border-bottom-width: ${0};
    border-color: ${0};
    color: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    line-height: ${0};
    padding: ${0};

    strong {
      font-weight: ${0};
    }
  }

  .${0} {
    &[aria-level="2"] .${0} {
      font-size: ${0};
      padding: ${0};

      :first-child {
        padding-left: ${0};
      }
    }

    :nth-child(even) .${0} {
      background-color: ${0};
    }
    
    :nth-child(odd) .${0} {
      background-color: ${0};
    }
  }

  .${0} {
    border-width: 0;
    vertical-align: top;
    background-color: ${0};
    color: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    line-height: ${0};
    padding: ${0};

    strong {
      font-weight: ${0};
    }
  }

  .${0} {
    display: block;
    position: static;
    appearance: none;
    padding: 0;
    margin: 0;
    border: 0;
    color: ${0};
    background: none;
    cursor: pointer;
    font-size: ${0};
    outline: none;

    path {
      transition: color 0.2s ease-in-out;
    }

    .${0} {
      position: relative;
      display: block;
      z-index: 2;
    }

    ::before {
      content: '';
      position: absolute;
      display: block;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      border-radius: ${0} ${0} 0 0;
      border-width: ${0};
      border-bottom-width: 0;
      border-style: solid;
      border-color: transparent;
      background-color: transparent;
      box-sizing: border-box;
      z-index: 1;
      transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
    }

    :hover {
      color: ${0};

      ::before {
        background-color: ${0};
      }
    }

    :focus {
      outline: none;

      ::before {
        border-color: ${0};
      }
    }

    :active {
      color: ${0};

      ::before {
        background-color: ${0};
      }
    }

    :disabled {
      color: ${0};
    }
  }

  .${0} {
    color: ${0};

    ::before {
      background-color: ${0};
    }
  }

  .${0} {
    text-align: left;
  }

  .${0} {
    text-align: center;
  }

  .${0} {
    text-align: right;
  }

  .${0} {
    display: none;

    @media (min-width: ${0}) {
      display: table-cell;
    }
  }

  .${0} {
    display: none;

    @media (min-width: ${0}) {
      display: table-cell;
    }
  }

  .${0} {
    display: none;

    @media (min-width: ${0}) {
      display: table-cell;
    }
  }

  .${0} {
    display: none;

    @media (min-width: ${0}) {
      display: table-cell;
    }
  }
`), selectFromTheme('spacingSmallDefault'), h1ClassName, h3ClassName, selectFromTheme('spacingXXSmallDefault'), h2ClassName, paragraphClassName, selectFromTheme('spacingLargeDefault'), tableCaptionClassName, h3Css, selectFromTheme('spacingXXSmallDefault'), tableHeadCellClassName, selectFromTheme('colorBackgroundTableRowEven'), selectFromTheme('thicknessTableBorderDefault'), selectFromTheme('colorTableBorder'), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyTableHead'), selectFromTheme('fontSizeTableHeadDefault'), selectFromTheme('fontWeightTableHead'), selectFromTheme('lineHeightTableHeadDefault'), selectFromTheme('paddingTableHeadDefault'), tableHeadCellWrapperClassName, tableHeadCellTitleClassName, tableHeadCellActionClassName, selectFromTheme('spacingXXXSmallDefault'), tableBodyCellClassName, selectFromTheme('thicknessTableBorderDefault'), selectFromTheme('colorTableBorder'), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyTableBody'), selectFromTheme('fontSizeTableBodyDefault'), selectFromTheme('fontWeightTableBody'), selectFromTheme('lineHeightTableBodyDefault'), selectFromTheme('paddingTableBodyDefault'), selectFromTheme('fontWeightTableBodyStrong'), tableRowClassName, tableBodyCellClassName, selectFromTheme('fontSizeTableBodyLevel2Default'), selectFromTheme('paddingTableBodyLevel2Default'), selectFromTheme('indentTableBodyLevel2Default'), tableBodyCellClassName, selectFromTheme('colorBackgroundTableRowEven'), tableBodyCellClassName, selectFromTheme('colorBackgroundTableRowOdd'), tableFooterCellClassName, selectFromTheme('colorBackgroundTableRowEven'), selectFromTheme('colorTextPrimaryMain'), selectFromTheme('fontFamilyTableFooter'), selectFromTheme('fontSizeTableFooterDefault'), selectFromTheme('fontWeightTableFooter'), selectFromTheme('lineHeightTableFooterDefault'), selectFromTheme('paddingTableFooterDefault'), selectFromTheme('fontWeightTableFooterStrong'), tableSortButtonClassName, selectFromTheme('colorIconButtonDefault'), selectFromTheme('fontSizeBodyRegularDefault'), iconClassName, selectFromTheme('borderRadiusIconButtonDefault'), selectFromTheme('borderRadiusIconButtonDefault'), selectFromTheme('borderWidthIconButtonDefault'), selectFromTheme('colorIconButtonHover'), selectFromTheme('colorBackgroundInteractiveListItemHover'), selectFromTheme('colorTextPrimaryAccent'), selectFromTheme('colorIconButtonActive'), selectFromTheme('colorBackgroundInteractiveListItemActive'), selectFromTheme('colorIconButtonDisabled'), tableSortButtonActiveClassName, selectFromTheme('colorIconButtonActive'), selectFromTheme('colorBackgroundInteractiveListItemHover'), tableCellLeftAlignClassName, tableCellCenterAlignClassName, tableCellRightAlignClassName, tableCellP2ClassName, selectFromTheme('breakpointMedium'), tableCellP3ClassName, selectFromTheme('breakpointWide'), tableCellP4ClassName, selectFromTheme('breakpointExtraWide'), tableCellP5ClassName, selectFromTheme('breakpointUltraWide'));

/**
 * Renders all the cells of a table row.
 *
 * @param Component - The component to use for each table cell
 * @param columns - The table columns configuration
 * @param rowCells - The configuration for each cell in the table row
 * @param key - The base to form a unique React node key
 */
function renderRowCells(Component,
// eslint-disable-line @typescript-eslint/naming-convention
columns, rowCells, key) {
  let currentColIndex = 0;
  return _mapInstanceProperty(rowCells).call(rowCells, function (cell) {
    const {
      priority,
      textAlign
    } = columns[currentColIndex];
    let content;
    let span;
    let rowHeader;

    // Treat the cell as a cell configuration object
    if (typeof cell === 'object' && cell !== null && ! /*#__PURE__*/React.isValidElement(cell)) {
      var _cell$span;
      // @ts-expect-error the content property will not be available in React nodes
      content = cell.content;

      // @ts-expect-error the content property will not be available in React nodes
      span = (_cell$span = cell.span) !== null && _cell$span !== void 0 ? _cell$span : 1;

      // @ts-expect-error the content property will not be available in React nodes
      rowHeader = cell.rowHeader;

      // Treat the cell as a React node
    } else {
      content = cell;
      span = 1;
    }
    currentColIndex += span;
    const currentKey = `${key}-cell-${currentColIndex}`;
    return /*#__PURE__*/jsx(Component, {
      priority: priority,
      rowHeader: rowHeader,
      span: span,
      textAlign: textAlign,
      children: content
    }, currentKey);
  });
}

/**
 * Renders all the table header cells.
 *
 * @param columns - The table columns configuration
 */
function renderHeader(props) {
  const {
    columns,
    getSortButtonLabel,
    id,
    onSortColumnClick,
    sortedBy,
    sortDirection
  } = props;
  return /*#__PURE__*/jsx("thead", {
    children: /*#__PURE__*/jsx(TableRow, {
      children: _mapInstanceProperty(columns).call(columns, function (cell, index) {
        const {
          // @ts-expect-error the property is optional
          defaultSortDirection,
          key,
          priority,
          sortable,
          textAlign,
          title
        } = cell;
        return /*#__PURE__*/jsx(TableHeadCell, {
          columnKey: key,
          defaultSortDirection: defaultSortDirection,
          getSortButtonLabel: getSortButtonLabel,
          id: typeof id === 'string' ? `${id}-${key !== null && key !== void 0 ? key : index}` : undefined,
          priority: priority,
          sortDirection: typeof sortedBy === 'string' && sortedBy === key ? sortDirection : undefined,
          sortable: sortable,
          textAlign: textAlign,
          onSortColumnClick: onSortColumnClick,
          children: title
        }, `table-header-${key !== null && key !== void 0 ? key : index}`);
      })
    })
  });
}

/**
 * Renders a table row and its children if available.
 *
 * @param columns - The table columns configuration
 * @param row - The data to display in the table row
 * @param index - The index of the table row in the current level
 * @param level - The current level of the table data
 * @param key - The base to form a unique React node key
 */
function renderTableBodyRow(columns, row, index, level, key) {
  var _context;
  const position = index + 1;
  const currentKey = `${key}-${position}`;
  let rowContent;
  let children;

  // Treat the row as an array of cell data
  if (_Array$isArray(row)) {
    rowContent = row;

    // Treat the row as a row configuration object
  } else {
    rowContent = row.content;
    children = row.children;
  }
  return /*#__PURE__*/jsxs(React.Fragment, {
    children: [/*#__PURE__*/jsx(TableRow, {
      level: level,
      children: renderRowCells(TableBodyCell, columns, rowContent, currentKey)
    }), _Array$isArray(children) ? _mapInstanceProperty(_context = children).call(_context, function (child, childIndex) {
      return renderTableBodyRow(columns, child, childIndex, level + 1, currentKey);
    }) : null]
  }, currentKey);
}

/**
 * Renders all the table body rows and columns.
 *
 * @param columns - The table columns configuration
 * @param data - The data to display in the table
 */
function renderBody(props) {
  var _context2;
  const {
    columns,
    data
  } = props;
  return /*#__PURE__*/jsx("tbody", {
    children: _mapInstanceProperty(_context2 = data).call(_context2, function (row, index) {
      return renderTableBodyRow(columns, row, index, 1, 'table-body');
    })
  });
}

/**
 * Renders all the table footer cells.
 *
 * @param columns - The table columns configuration
 * @param footer - The configuration for each cell in the table footer
 */
function renderFooter(props) {
  const {
    columns,
    footer
  } = props;
  if (!_Array$isArray(footer)) {
    return null;
  }
  return /*#__PURE__*/jsx("tfoot", {
    children: /*#__PURE__*/jsx(TableRow, {
      children: renderRowCells(TableFooterCell, columns, footer, 'table-footer')
    })
  });
}

/**
 * Renders a responsive data table which is able to hide different columns as necessary to fit the viewport.
 * Which columns render under different viewports will depend on the `priority` level (a number from 1 to 5, with 1 being the highest priority and the default) defined in the column configuration.
 *
 * If the table does not fit the viewport if will become horizontally scrollable.
 *
 * *The configuration for the columns, data and footer should be memoized or declared outside the render function to avoid needless re-renders.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Table } from '@thisisbud/bui-react-table'
 *
 * function Example() {
 *   const columns = useMemo(function () {
 *     return [{ title: 'Transaction' }, { textAlign: 'right', title: 'Amount' }]
 *   }, [])
 *   const data = useMemo(function () {
 *     return [[{ content: 'Wealthify LM452783' }, { content: '£3000.00' }], [{ content: 'Howdens' }, { content: '£3562.00' }]]
 *   }, [])
 *   const footer = useMemo(function () {
 *     return [{ content: 'Total' }, { content: '£6352.00' }]
 *   }, [])
 *
 *   return (
 *     <Table
 *       caption='Foobar'
 *       columns={columns},
 *       data={data}
 *       footer={footer}
 *     />
 *   )
 * }
 * ```
 */
function Table(props) {
  const {
    caption,
    className,
    id
  } = props;
  return /*#__PURE__*/jsx(StyledTableWrapper, {
    className: classnames(tableClassName, className),
    id: id,
    children: /*#__PURE__*/jsxs("table", {
      children: [typeof caption === 'string' ? /*#__PURE__*/jsx(TableCaption, {
        children: caption
      }) : null, renderHeader(props), renderBody(props), renderFooter(props)]
    })
  });
}
Table.displayName = 'Table';

export { Table, tableBodyCellClassName, tableCaptionClassName, tableCellCenterAlignClassName, tableCellClassName, tableCellLeftAlignClassName, tableCellP1ClassName, tableCellP2ClassName, tableCellP3ClassName, tableCellP4ClassName, tableCellP5ClassName, tableCellRightAlignClassName, tableClassName, tableFooterCellClassName, tableHeadCellActionClassName, tableHeadCellClassName, tableHeadCellTitleClassName, tableHeadCellWrapperClassName, tableRowClassName, tableSortButtonActiveClassName, tableSortButtonClassName };
